import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";
import { storySteps } from "../../data/demoContent";
import { useDemo } from "../../context/DemoContext";
import { useIsDesktop } from "../../hooks/useMedia";
import { PhoneDemo } from "./PhoneDemo";
import { ModeSwitch } from "./ModeSwitch";
import { Words } from "../Words";

const StepBlock = ({ step, index, active, onActive, onPick }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { margin: "-48% 0px -48% 0px" });
  useEffect(() => {
    if (inView) onActive(index);
  }, [inView, index, onActive]);
  return (
    <li ref={ref} className="flex min-h-[72vh] items-center" data-testid={`story-step-${index + 1}`} data-active={active}>
      <button type="button" onClick={() => onPick(index)} className={`step-block text-left ${active ? "is-active" : ""}`}>
        <span className="text-[13px] font-semibold text-[#7866E8]">0{index + 1}</span>
        <span className="mt-3 block text-[34px] font-bold leading-[1.05] tracking-[-0.035em] text-[#08090D] xl:text-[40px]">{step.title}</span>
        <span className="mt-4 block max-w-[440px] text-[18px] leading-relaxed text-[#555a6e]">{step.body}</span>
      </button>
    </li>
  );
};

const MobileCaptions = ({ onPick, current }) => (
  <ol className="mt-8 space-y-2" aria-label="How it works">
    {storySteps.map((s, i) => (
      <li key={s.title}>
        <button
          type="button"
          onClick={() => onPick(i)}
          data-testid={`story-caption-${i + 1}`}
          className={`glass glass-light w-full rounded-2xl px-4 py-3 text-left transition-opacity ${current === i ? "opacity-100" : "opacity-70"}`}
        >
          <span className="text-[12px] font-semibold text-[#7866E8]">0{i + 1}</span>
          <span className="ml-2 text-[15px] font-semibold text-[#08090D]">{s.title}</span>
          <span className="mt-1 block text-[13px] leading-snug text-[#555a6e]">{s.body}</span>
        </button>
      </li>
    ))}
  </ol>
);

export default function StoriesSection() {
  const desktop = useIsDesktop();
  const { applyState, shouldStepApply, activeStep, markInteraction } = useDemo();
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    if (current == null) return;
    activeStep.current = current;
    if (shouldStepApply(current)) applyState(storySteps[current].state);
  }, [current, applyState, shouldStepApply, activeStep]);

  const pick = (i) => {
    applyState(storySteps[i].state);
    markInteraction(i);
    setCurrent(i);
  };

  return (
    <section id="stories" className="relative bg-[#F7F8FB] pb-24 pt-28 lg:pb-32 lg:pt-36" data-testid="stories-section">
      <div className="stories-wash pointer-events-none absolute inset-x-0 top-0 h-[70vh]" aria-hidden />
      <div className="relative mx-auto max-w-[1240px] px-5 lg:px-10">
        <p className="eyebrow">02 · Stories become conversations</p>
        <h2 className="h-section mt-4 text-[#08090D]" data-testid="stories-headline">
          <Words text="A Story." />
          <br />
          <Words text="A Conversation." start={2} className="text-[#777B8D]" />
          <br />
          <Words text="A Connection." start={4} className="text-gradient" />
        </h2>
        <p className="mt-6 max-w-[520px] text-[18px] leading-relaxed text-[#4c5064] sm:text-[20px]">
          See a moment. Start a conversation. Stay connected without switching screens.
        </p>

        {desktop ? (
          <div className="mt-6 grid grid-cols-[1fr_440px] gap-12 xl:gap-20">
            <ol className="pb-[10vh]">
              {storySteps.map((s, i) => (
                <StepBlock key={s.title} step={s} index={i} active={current === i} onActive={setCurrent} onPick={pick} />
              ))}
            </ol>
            <div>
              <div className="sticky top-[84px] flex flex-col items-center pt-4">
                <PhoneDemo />
                <ModeSwitch />
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-12 flex flex-col items-center">
            <PhoneDemo />
            <ModeSwitch />
            <MobileCaptions onPick={pick} current={current} />
          </div>
        )}
      </div>
    </section>
  );
}
